import React, { useMemo, useRef, useState } from 'react';
import { format, parseISO } from 'date-fns';
import LocationEditor from './LocationEditor';

const columns = [
  { key: 'publish', label: 'Publish', width: 62 },
  { key: 'id', label: 'ID', width: 90 },
  { key: 'title', label: 'Title', width: 285 },
  { key: 'category', label: 'Category', width: 115 },
  { key: 'start_local', label: 'Start local', width: 104 },
  { key: 'true_end', label: 'True end', width: 104 },
  { key: 'check_timings', label: 'Check timings', width: 100 },
  { key: 'attendance', label: 'Attendance', width: 100 },
  { key: 'venue_name', label: 'Venue name', width: 170 },
  { key: 'postcode', label: 'Postcode', width: 92 },
  { key: 'venue_formatted_address', label: 'Address', width: 250 },
  { key: 'lat', label: 'Latitude', width: 105 },
  { key: 'lon', label: 'Longitude', width: 105 },
  { key: 'actions', label: '', width: 98 }
];

const categoryClass = (category) => `category-pill category-${String(category || 'other').toLowerCase().replace(/[^a-z-]/g, '')}`;

const getLeavingWindow = (size, end) => {
  const windows = { local: [-2, 10], event: [-5, 15], main: [-8, 20], major: [-10, 30] };
  const [before, after] = windows[String(size || '').toLowerCase()] || [0, 0];
  const endTime = new Date(end).getTime();
  return {
    leaving_start: new Date(endTime + before * 60000).toISOString(),
    leaving_end: new Date(endTime + after * 60000).toISOString()
  };
};

const londonTime = (iso) => new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/London',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23'
}).format(new Date(iso));

const londonDate = (iso) => new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit'
}).format(new Date(iso));

const searchEvent = (event) => {
  const query = `${event.title} London ${londonDate(event.start_local)}`;
  window.open(`https://www.google.com/search?q=${encodeURIComponent(query)}`, '_blank', 'noopener,noreferrer');
};

const utcFromLondonTime = (iso, time) => {
  const date = new Date(iso);
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit'
  }).formatToParts(date).reduce((result, part) => ({ ...result, [part.type]: part.value }), {});
  const candidate = new Date(`${parts.year}-${parts.month}-${parts.day}T${time}:00Z`);
  const londonAtCandidate = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London', timeZoneName: 'longOffset', hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
  }).formatToParts(candidate).find((part) => part.type === 'timeZoneName')?.value || 'GMT';
  const offset = londonAtCandidate === 'GMT' ? 0 : Number(londonAtCandidate.match(/GMT([+-])(\d{2}):(\d{2})/)?.[2] || 0) * (londonAtCandidate.includes('-') ? -1 : 1) * 60;
  return new Date(candidate.getTime() - offset * 60000).toISOString();
};

function compareValues(left, right) {
  if (typeof left === 'boolean' || typeof right === 'boolean') {
    return Number(Boolean(left)) - Number(Boolean(right));
  }
  const leftNumber = Number(left);
  const rightNumber = Number(right);
  if (left !== '' && right !== '' && Number.isFinite(leftNumber) && Number.isFinite(rightNumber)) {
    return leftNumber - rightNumber;
  }
  return String(left ?? '').localeCompare(String(right ?? ''), undefined, {
    numeric: true,
    sensitivity: 'base'
  });
}

export default function EventTable({ events, onDeleteEvent, onDeleteSelected, onEventUpdate, selectedEventIds, selectedDays, onToggleEvent, onToggleDay, selectionLabel = 'Select' }) {
  const groups = useMemo(() => events.reduce((result, event) => {
    const key = londonDate(event.start_local);
    (result[key] ||= []).push(event);
    return result;
  }, {}), [events]);
  const dates = Object.keys(groups).sort();
  const [activeDate, setActiveDate] = useState();
  const [locationEvent, setLocationEvent] = useState(null);
  const [sort, setSort] = useState({ key: 'start_local', direction: 'asc' });
  const [columnWidths, setColumnWidths] = useState(() => Object.fromEntries(columns.map((column) => [column.key, column.width])));
  const resizeRef = useRef(null);
  const date = activeDate && groups[activeDate] ? activeDate : dates[0];
  const visibleEvents = useMemo(() => [...(groups[date] || [])].sort((left, right) => {
    const result = compareValues(left[sort.key], right[sort.key]);
    return sort.direction === 'asc' ? result : -result;
  }), [groups, date, sort]);
  const update = (event, field, value) => onEventUpdate({
    ...event,
    [field]: value,
    ...(field === 'true_end' ? getLeavingWindow(event.size, value) : {})
  });
  const updateSort = (key) => setSort((current) => current.key === key
    ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
    : { key, direction: 'asc' });
  const sortIndicator = (key) => sort.key === key ? (sort.direction === 'asc' ? ' ▲' : ' ▼') : '';
  const startResize = (event, key) => {
    event.preventDefault();
    const startWidth = columnWidths[key];
    const startX = event.clientX;
    const resize = (moveEvent) => setColumnWidths((current) => ({ ...current, [key]: Math.max(54, startWidth + moveEvent.clientX - startX) }));
    const stopResize = () => {
      window.removeEventListener('pointermove', resize);
      window.removeEventListener('pointerup', stopResize);
      resizeRef.current = null;
    };
    resizeRef.current = stopResize;
    window.addEventListener('pointermove', resize);
    window.addEventListener('pointerup', stopResize);
  };

  return (
    <section className="table-panel">
      {onDeleteSelected && <div className="event-table-actions"><span>{(selectedEventIds || []).length} selected</span><button type="button" className="delete" disabled={!selectedEventIds?.length} onClick={onDeleteSelected}>Delete selected</button></div>}
      <div className="date-tabs">
        {dates.map((item) => (
          <div key={item} className="date-tab">
            {onToggleDay && <input
              type="checkbox"
              checked={(selectedDays || []).includes(item)}
              onChange={() => onToggleDay(item)}
              title="Select or clear all events on this day"
              aria-label={`Select all events on ${item}`}
            />}
            <button className={item === date ? 'active' : ''} onClick={() => setActiveDate(item)}>
              {format(parseISO(item), 'EEE dd')}
            </button>
          </div>
        ))}
      </div>
      <div className="table-scroll">
        <table className="event-table">
          <colgroup>{columns.map((column) => <col key={column.key} style={{ width: columnWidths[column.key] }} />)}</colgroup>
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column.key} className="resizable-header">
                  {column.key !== 'actions' && <button className="sort-button" onClick={() => updateSort(column.key)}>{column.label}{sortIndicator(column.key)}</button>}
                  <span className="column-resizer" role="separator" aria-orientation="vertical" aria-label={`Resize ${column.label || 'actions'} column`} onPointerDown={(event) => startResize(event, column.key)} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleEvents.map((event) => (
              <tr key={event.id}>
                <td><input type="checkbox" checked={(selectedEventIds || []).includes(event.id)} onChange={() => onToggleEvent(event.id)} aria-label={`${selectionLabel} ${event.title}`} /></td>
                <td className="mono">{event.id.slice(0, 8)}</td>
                <td><strong>{event.icon} {event.title}</strong><small>{event.labels.join(', ')}</small><button className="web-search" title="Search event on Google" aria-label={`Search ${event.title} on Google`} onClick={() => searchEvent(event)}>⌕</button></td>
                <td><span className={categoryClass(event.category)}>{event.category || 'other'}</span></td>
                <td><input type="time" value={londonTime(event.start_local)} onChange={(e) => update(event, 'start_local', utcFromLondonTime(event.start_local, e.target.value))} /></td>
                <td><input type="time" value={londonTime(event.true_end)} onChange={(e) => update(event, 'true_end', utcFromLondonTime(event.true_end, e.target.value))} /></td>
                <td><input type="checkbox" checked={event.check_timings === '‼'} aria-label="Check timings" onChange={(e) => update(event, 'check_timings', e.target.checked ? '‼' : '')} /></td>
                <td><span>{event.attendance || 0}</span><small>{event.size}</small></td>
                <td>{event.venue_name || '-'}</td>
                <td>{event.postcode || '-'}</td>
                <td className="address">{event.venue_formatted_address || '-'}</td>
                <td>{event.lat ?? '-'}</td>
                <td>{event.lon ?? '-'}</td>
                <td>
                  <button className="map-link" onClick={() => setLocationEvent(event)}>Edit</button>
                  <button className="delete" onClick={() => onDeleteEvent(event.id)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {locationEvent && <LocationEditor event={locationEvent} onSave={(updated) => { onEventUpdate(updated); setLocationEvent(null); }} onClose={() => setLocationEvent(null)} />}
    </section>
  );
}
